import { withProperties } from '$/utils/type';
import React, { ReactNode } from 'react';
import { AssetInput, MediaHint, MediaRef, MediaRefFragment } from '$/GraphQL/schema/schema';
import { css } from '@emotion/react';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import {
  WithUploadChildrenProps,
  WithUploadImage,
} from '$/components/WithUploadImage/WithUploadImage';
import tw from 'twin.macro';
import { PlusIcon } from '$/images/Icons';
import { FloatingRemoveButton } from '../Button/Button';

// import fetch from "isomorphic-fetch"
// https://medium.com/@blackwright/browser-file-uploads-to-s3-using-fetch-46a53d106e11

export interface UploadProps {
  id?: string;
  className?: string;
  asset: AssetInput;
  error?: string | null;
  mode?: PageMode;
  allowVideo?: boolean;
  roundedCorners?: boolean;
  value?: MediaRefFragment | null;
  onChange?: (url: MediaRefFragment | null | undefined) => void;
  render?: (props: WithUploadChildrenProps) => React.ReactNode;
}

type ChildProps = Omit<UploadProps, 'asset'> & {
  children?: ReactNode | undefined;
  asset?: AssetInput;
  error?: string | null;
  componentName?: string;
};

const Child: React.FC<ChildProps> = ({ componentName, children, ...props }) => {
  return (
    <React.Fragment>
      {React.Children.toArray(children)
        .filter((child: any) => child.type.name === componentName)
        .map((element: any) => React.cloneElement(element, props))}
    </React.Fragment>
  );
};

const Upload: React.FC<UploadProps> = ({ className, asset, render, value, ...props }) => {
  const disabled = props.mode !== PageMode.edit;

  const onChange = React.useCallback(
    (val: MediaRef | null | undefined) => {
      props.onChange && props.onChange(val);
    },
    [props]
  );

  return (
    <WithUploadImage
      mode={props.mode}
      allowVideo={props.allowVideo}
      value={value}
      name='profile'
      disabled={disabled}
      onChange={onChange}
      asset={asset}
      className={`flex ${
        props.mode === PageMode.edit ? 'cursor-pointer border border-color-pagebuilder-frame' : ''
      } ${props.roundedCorners && 'rounded'} ${className}`}
    >
      {(renderProps: WithUploadChildrenProps): React.ReactNode => {
        const { loading, value, onRemove, error } = renderProps;
        if (render) return render(renderProps);
        return (
          <div className='flex relative'>
            {loading && (
              <div
                className='loading'
                css={css`
                  width: ${asset.container!.width}px;
                  height: ${asset.container!.height}px;
                `}
              >
                <Child asset={asset} componentName='Loading'>
                  {props.children}
                </Child>
              </div>
            )}
            {!value?.url && !loading && (
              <>
                <Child asset={asset} error={error} componentName='Empty'>
                  {props.children}
                </Child>
                {asset.container?.hint === MediaHint.Profile && !disabled && (
                  <div
                    css={css`
                      ${tw`
                      cursor-pointer outline-none appearance-none shadow
                      bg-coral-1 rounded-full w-1_5e h-1_5e
                      flex items-center justify-center`}
                      position: absolute;
                      top: -9px;
                      right: -9px;
                    `}
                  >
                    <PlusIcon className='text-white w-1e h-1e' />
                  </div>
                )}
              </>
            )}
            {value && value.url && !loading && (
              <>
                <Child value={value} asset={asset} id={props.id} componentName='Success'>
                  {props.children}
                </Child>
                {props.mode === PageMode.edit && <FloatingRemoveButton onClick={onRemove} />}
              </>
            )}
          </div>
        );
      }}
    </WithUploadImage>
  );
};

const Success: React.FC<ChildProps> = ({ children, ...props }) => {
  return (
    <div className={`image-show larger font-body ${props.className}`}>
      {React.cloneElement(children as any, props)}
    </div>
  );
};

const Loading: React.FC<ChildProps> = ({ children, ...props }) => {
  return (
    <div className='h-full w-full flex justify-center'>
      {children ? (
        React.cloneElement(children as any, props)
      ) : (
        <img className='flex w-1_5e m-1e' src={require('$/images/spinner.svg').default} />
      )}
    </div>
  );
};

const Empty: React.FC<ChildProps> = ({ children, ...props }) => (
  <div className='h-full w-full flex'>{React.cloneElement(children as any, props)}</div>
);

export default withProperties(Upload, { Success, Empty, Loading });
